import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { formatDateTime } from '../lib/utils';
import { FileStack, Loader2, Plus, ChevronRight, AlertCircle } from 'lucide-react';

export const ARTIFACT_TYPE_LABELS = {
  framework: 'Framework', vision_deck: 'Vision Deck', proposal: 'Proposal', workshop: 'Workshop', custom: 'Custom',
};

export function EngagementCreatedWork({ engagementId, organizationId, canEdit, onCountChange }) {
  const navigate = useNavigate();
  const { get, post } = useApi();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('loading');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', artifact_type: 'framework' });
  const [creating, setCreating] = useState(false);
  const returnState = { state: { returnTo: `/engagements/${engagementId}` } };

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await get('/vision-decks', { engagement_id: engagementId });
      setItems(res || []); setStatus('ready'); onCountChange?.((res || []).length);
    } catch (e) { setStatus('error'); }
  }, [get, engagementId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Please enter a title'); return; }
    setCreating(true);
    try {
      const deck = await post('/vision-decks', {
        name: form.name.trim(), artifact_type: form.artifact_type,
        engagement_id: engagementId, ...(organizationId ? { organization_id: organizationId } : {}),
      });
      toast.success(`${ARTIFACT_TYPE_LABELS[form.artifact_type]} created`);
      navigate(`/vision-decks/${deck.id}/edit`, returnState);
    } catch (err) { toast.error(err.message || 'Could not create'); setCreating(false); }
  };

  return (
    <Card className="rounded-sm border" data-testid="eng-created-work">
      <CardHeader className="pb-4 flex flex-row items-center justify-between gap-3">
        <CardTitle className="text-base font-medium">Created Work ({items.length})</CardTitle>
        {canEdit && (
          <Button size="sm" variant="outline" className="rounded-sm" data-testid="eng-create-work-btn"
            onClick={() => { setForm({ name: '', artifact_type: 'framework' }); setOpen(true); }}>
            <Plus className="h-4 w-4 mr-1.5" />Create
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {status === 'loading' && <div className="flex justify-center py-8" data-testid="eng-work-loading"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {status === 'error' && (
          <div className="text-center py-8" data-testid="eng-work-error">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-destructive" />
            <p className="text-sm text-muted-foreground mb-3">Couldn't load created work</p>
            <Button size="sm" variant="outline" className="rounded-sm" onClick={load} data-testid="eng-work-retry">Try again</Button>
          </div>
        )}
        {status === 'ready' && items.length === 0 && (
          <div className="text-center py-8" data-testid="eng-work-empty">
            <FileStack className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">No work created for this engagement yet</p>
            {canEdit && <p className="text-xs text-muted-foreground mt-1">Use Create to start a framework, deck, proposal or workshop</p>}
          </div>
        )}
        {status === 'ready' && items.length > 0 && (
          <div className="space-y-2">
            {items.map((d) => (
              <button key={d.id} type="button" onClick={() => navigate(`/vision-decks/${d.id}/edit`, returnState)}
                className="w-full flex items-center justify-between gap-3 p-3 bg-muted/30 hover:bg-muted/60 rounded-sm text-left transition-colors" data-testid={`eng-work-row-${d.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{d.name}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className="rounded-sm text-xs" data-testid={`eng-work-type-${d.id}`}>{ARTIFACT_TYPE_LABELS[d.artifact_type] || 'Vision Deck'}</Badge>
                    <Badge className={`rounded-sm text-xs capitalize ${d.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`} data-testid={`eng-work-status-${d.id}`}>{d.status || 'draft'}</Badge>
                    <span className="text-xs text-muted-foreground">Updated {formatDateTime(d.updated_at || d.created_at)}</span>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(v) => !creating && setOpen(v)}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader><DialogTitle>Create Work</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Type</Label>
              <Select value={form.artifact_type} onValueChange={(v) => setForm({ ...form, artifact_type: v })}>
                <SelectTrigger className="rounded-sm" data-testid="eng-work-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ARTIFACT_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`eng-work-type-option-${v}`}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Title</Label>
              <Input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Conflict Styles Framework" className="rounded-sm" data-testid="eng-work-name-input" />
            </div>
            <p className="text-xs text-muted-foreground">Opens in the builder, linked to this engagement{organizationId ? ' and its organization' : ''}.</p>
            <DialogFooter>
              <Button type="submit" disabled={creating} className="rounded-sm" data-testid="eng-work-create-submit">
                {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create &amp; Open Builder
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
